import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Partytown } from '@builder.io/partytown/react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { initMonitoring } from '@repo/monitoring';
import { ENVIRONMENT, SENTRY_DSN } from './assets/env_constants';
import { Button, Typography } from 'components';
import App from './App';

// Initialize monitoring with universal error rules
initMonitoring({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,

  // To test error reporting in sandbox/development, uncomment:
  // captureErrors: true,

  errorRules: {
    captureOn5xx: true,
    captureOn4xx: false,
    captureNetworkErrors: false,
    captureTimeouts: true,
    slowRequestThreshold: 4000,
    largeResponseThreshold: 2_000_000,
  },
  sentryOptions: {
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
  },
});

const router = createBrowserRouter([
  {
    path: '/*',
    element: <App />,
  },
]);

function ServiceWorkerWrapper({ children }: { children: React.ReactNode }) {
  const { updateServiceWorker } = useRegisterSW({
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    onRegistered(r: any) {
      r &&
        setInterval(
          () => {
            r.update();
          },
          1000 * 60 * 60,
        ); //1 hour
    },
    onNeedRefresh() {
      toast.dismiss();
      toast(
        <div>
          <Typography variant='body-s'>
            This page has been updated. Reload to see the latest version.
          </Typography>
          <div className='flex gap-3'>
            <Button
              label='Reload'
              onClick={async () => {
                await updateServiceWorker();
                await toast.dismiss();
              }}
            />
          </div>
        </div>,
        {
          autoClose: false,
          closeButton: false,
        },
      );
    },
  });

  return <>{children}</>;
}

const render = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Failed to find the root element');

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ServiceWorkerWrapper>
        <Partytown forward={['dataLayer.push']} />
        <RouterProvider router={router} />
      </ServiceWorkerWrapper>
    </React.StrictMode>,
  );
};

render();
